import { useState } from "react";
import { Link } from "react-router-dom";
import { ARTICLES, COLUMNS, HOT_LIST } from "../../../mocks/mockContent";
import { formatCount } from "../../../utils/format";
import Reveal from "../../../components/feature/Reveal";

export default function Feed() {
  const [active, setActive] = useState<string>("all");

  const list =
    active === "all" ? ARTICLES.slice(0, 6) : ARTICLES.filter((a) => a.category === active).slice(0, 6);

  return (
    <section aria-label="最新瓜田" className="border-t hairline bg-background-100/60">
      <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold tracking-wide text-primary-700">
              <i className="ri-sun-line" aria-hidden="true"></i>
              每日新瓜
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">最新瓜田</h2>
          </div>
          {/* segmented filter */}
          <div className="inline-flex flex-wrap gap-1 rounded-full bg-background-200/70 p-1" role="tablist" aria-label="栏目筛选">
            {COLUMNS.map((c) => (
              <button
                key={c.key}
                role="tab"
                aria-selected={active === c.key}
                onClick={() => setActive(c.key)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                  active === c.key
                    ? "bg-background-50 font-semibold text-primary-700 shadow-soft"
                    : "text-foreground-600 hover:text-primary-700"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-12">
          {/* feed */}
          <div className="lg:col-span-8">
            <ol className="divide-y hairline">
              {list.map((a, i) => (
                <li key={a.id}>
                  <Reveal>
                    <Link to={`/read/${a.id}`} className="group flex gap-4 py-5">
                      <span className="w-8 shrink-0 font-display text-2xl font-bold text-background-300 transition-colors group-hover:text-primary-400">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <img
                        src={a.cover}
                        alt={a.title}
                        className="hidden h-24 w-36 shrink-0 rounded-lg object-cover object-top sm:block"
                      />
                      <div className="min-w-0 flex-1">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
                          {a.category}
                        </span>
                        <h3 className="mt-1.5 line-clamp-2 font-display text-lg font-semibold leading-snug text-foreground-950 group-hover:text-primary-700">
                          {a.title}
                        </h3>
                        <p className="mt-1 line-clamp-1 text-sm text-foreground-600">{a.summary}</p>
                        <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                          <span className="inline-flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{formatCount(a.read)}</span>
                          <span className="inline-flex items-center gap-1"><i className="ri-heart-line" aria-hidden="true"></i>{formatCount(a.likes)}</span>
                          <span>{a.date}</span>
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ol>

            <div className="mt-4 flex items-center justify-center">
              <Link
                to="/rexun"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                浏览全部瓜田
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>

          {/* hot sidebar */}
          <aside className="lg:col-span-4" aria-label="吃瓜热榜">
            <Reveal className="rounded-2xl border hairline bg-background-50 p-6">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-display text-lg font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
                  吃瓜热榜
                </h3>
                <Link to="/rexun" className="text-xs text-foreground-500 transition-colors hover:text-primary-700">查看全部</Link>
              </div>
              <ol className="mt-5 space-y-1">
                {HOT_LIST.map((h) => (
                  <li key={h.rank}>
                    <Link to={`/read/${h.id}`} className="group flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-background-100">
                      <span
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-display text-sm font-bold ${
                          h.rank <= 3
                            ? "bg-primary-500 text-background-50"
                            : "bg-secondary-100 text-secondary-900"
                        }`}
                      >
                        {h.rank}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
                          {h.title}
                        </p>
                        <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                          <span className="text-primary-600">{h.heat} 热度</span>
                          <span className="rounded bg-background-200 px-1.5 py-0.5 text-foreground-600">{h.tag}</span>
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>
            </Reveal>

            <Reveal delay={120} className="mt-6 overflow-hidden rounded-2xl bg-primary-500 p-6 text-background-50">
              <p className="flex items-center gap-2 text-sm font-semibold">
                <i className="ri-voiceprint-fill" aria-hidden="true"></i>
                瓜田一句话
              </p>
              <p className="mt-3 font-display text-lg font-semibold leading-7">
                看得越多，越要慢一点；聊得越热，越要真一点。
              </p>
              <Link to="/about#principles" className="mt-4 inline-flex items-center gap-1 text-sm font-medium underline-offset-4 hover:underline">
                了解内容原则
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </Reveal>
          </aside>
        </div>
      </div>
    </section>
  );
}